import { customFieldBuilderFr } from './custom-field-builder-i18n';
import { translationCenterFr } from './translation-center-i18n';
import { backupCenterFr, backupRestoreFr } from './backup-center-i18n';

/** Translations for the administration area; wired up in lib/i18n/en.ts. */
export const adminFr = {
  nav: {
    label: 'Administration',
    system: 'Système et télémétrie',
    errorLogs: "Centre d'erreurs",
    customFields: 'Champs personnalisés',
    auditVault: "Coffre-fort d'audit GoBD",
    dataHub: 'Importer et exporter',
    mailBackups: 'Envoi de courriels',
    backups: 'Centre de sauvegarde',
    whatsapp: 'WhatsApp',
    telephonyAi: 'Téléphonie et IA',
  },
  errorLogs: {
    title: "Centre d'erreurs",
    subtitle: 'Erreurs système enregistrées de manière centralisée avec leur contexte complet.',
    loadError: "Les journaux d'erreurs n'ont pas pu être chargés.",
    emptyTitle: 'Aucune erreur enregistrée',
    emptyHint: 'Aucune entrée ne correspond aux filtres sélectionnés.',
    kpi: {
      unresolved: 'Erreurs ouvertes',
      last24h: 'Dernières 24 heures',
    },
    filters: {
      search: 'Rechercher',
      searchPlaceholder: 'ID de suivi, message ou point de terminaison',
      httpStatus: 'État HTTP',
      from: 'De',
      to: 'À',
      unresolvedOnly: 'Non résolu uniquement',
      reset: 'Réinitialiser les filtres',
    },
    columns: {
      timestamp: 'Horodatage',
      status: 'Statut',
      trackingId: 'ID de suivi',
      user: 'Utilisateur',
      endpoint: 'Point d’accès',
      message: 'Messages',
      state: 'Traitement',
    },
    state: {
      open: 'Ouvrir',
      resolved: 'Résolu',
    },
    detail: {
      title: "Détails de l'erreur",
      ip: 'Adresse IP',
      client: 'Client',
      message: 'Messages',
      context: 'Contexte commercial',
      payload: 'Demander une charge utile',
      payloadHint:
        "Les mots de passe, les jetons et les coordonnées bancaires sont supprimés avant d'être stockés.",
      stackTrace: 'Trace de pile',
      notes: 'Note de résolution',
      resolved: "L'erreur a été marquée comme résolue.",
      reopened: "L'erreur a été rouverte.",
      resolvedBy: 'Résolu par {{name}} sur {{date}}.',
    },
    actions: {
      resolve: 'Marquer comme résolu',
      reopen: 'Réouvrir',
      openDocument: 'Ouvrir le document concerné',
      export: "Rapport de support à l'exportation",
    },
  },
  status: {
    OK: 'OK',
    WARNING: 'Avertissement',
    CRITICAL: 'Critique',
  },
  system: {
    title: 'Système et télémétrie',
    subtitle: "Application, base de données et serveur en un coup d'œil.",
    error: "La télémétrie n'a pas pu être chargée.",
    refresh: 'Actualiser',
    application: 'Demande',
    database: 'Base de données',
    server: 'Serveur',
    version: 'Version',
    commit: 'Valider',
    node: 'Node.js',
    environment: 'Environnement',
    uptime: 'Disponibilité',
    heap: 'Tas',
    dbSize: 'Taille de la base de données',
    connections: 'Connexions',
    streamClients: 'Clients en direct',
    scheduler: 'Tâches en arrière-plan',
    schedulerStopped: 'Ne fonctionne pas',
    auditEntries: "Entrées d'audit",
    cores: 'Cœurs de processeur',
    loadAverage: 'Charge moyenne',
    memory: 'Mémoire',
    backupDisk: 'Stockage de sauvegarde',
    unavailable: 'Indisponible',
    collectedAt: 'Collecté à',
    cpu: 'Charge du processeur',
    eventLoop: "Boucle d'événements {{value}} ms",
    cacheHit: 'Taux de réussite du cache {{value}} %',
    gauges: 'Utilisation',
    gaugesHint: 'Les valeurs sont actualisées toutes les 15 secondes.',
    features: 'Fonctions et modules',
    featuresHint: 'Disponibilité effective, erreurs de configuration et tâches en pause.',
    featuresActive: 'Actifs',
    featuresDisabled: 'Désactivés',
    featuresMisconfigured: 'Mal configurés',
    featuresBlocked: 'Dépendances bloquées',
    featuresPausedJobs: 'Tâches en pause',
    featuresLastChanges: 'Dernières modifications',
    featuresNoChanges: 'Aucune modification enregistrée.',
    featureActive: 'actif',
    featureInactive: 'inactif',
    quickActions: 'Actions rapides',
    quickActionsHint: 'Tâches de maintenance et caches.',
    clearCache: 'Vider le cache',
    cacheCleared: 'Suppression des fichiers mis en cache {{count}}.',
    maintenanceMode: 'Mode maintenance',
    maintenanceHint:
      "Bloque l'application pour tout le monde, à l'exception des adresses autorisées.",
    maintenanceMessage: 'Avis',
    allowedIps: 'Adresses IP autorisées',
    allowedIpsHint:
      'Séparées par des virgules, les plages CIDR telles que 10.0.0.0/8 sont autorisées.',
    maintenanceSaved: 'Paramètres enregistrés.',
  },
  customFields: {
    ...customFieldBuilderFr,
    translationCenter: translationCenterFr,
    title: 'Champs personnalisés',
    subtitle: 'Champs personnalisés par zone de données, sans modification du code.',
    create: 'Nouveau champ personnalisé',
    created: 'Champ personnalisé créé.',
    updated: 'Champ personnalisé enregistré.',
    archived: 'Champ personnalisé archivé.',
    empty: "Aucun champ personnalisé pour l'instant",
    emptyHint: 'Créez un champ pour capturer des informations supplémentaires.',
    dialogTitle: 'Créer un champ personnalisé',
    editTitle: 'Modifier le champ personnalisé',
    edit: 'Modifier',
    dialogDescription: 'Le champ apparaît dans les formulaires {{entity}}.',
    label: "Nom d'affichage\nClé",
    key: '',
    keyHint: 'Nom technique ; corrigé après la création.',
    description: 'Description',
    group: 'Catégorie',
    groupPlaceholder: 'Sélectionner une catégorie',
    type: 'Type de données',
    pattern: 'Expression régulière de validation',
    patternHint: 'Vérification facultative du format, par ex. ^[A-Z]{2}\\d{4}$.',
    options: 'Options',
    optionLabel: "Étiquette d'option",
    addOption: 'Ajouter une option',
    removeOption: "Supprimer l'option",
    noOptions: "Aucune option pour l'instant",
    required: 'Obligatoire',
    inPdf: 'Imprimer sur PDF',
    searchable: 'consultable',
    marketingSegmentationAllowed: 'Approuver pour la segmentation des newsletters',
    marketingSegmentationHint: 'Activer uniquement après examen et pour les champs non sensibles.',
    inList: 'Afficher dans les tableaux',
    inherit: 'Report aux documents de suivi',
    inheritHint: 'La valeur suit automatiquement le devis → commande → bon de livraison → facture.',
    inherited: 'Hérité',
    inItemTable: 'Afficher dans le tableau des éléments',
    inItemTableHint: "Apparaît sous la ligne de l'élément et sur le PDF.",
    copyFromProduct: "Copier la valeur du produit dans l'article",
    copyFromProductHint:
      "L'ajout du produit copie la valeur de ses données de base dans l'élément de document.",
    documentTypesTitle: 'Valable pour les types de documents',
    documentTypesHint: "Sans sélection, le champ s'applique à chaque type de document.",
    preview: 'Aperçu en direct',
    previewHint: "C'est ainsi que le champ apparaît dans le formulaire.",
    previewLabel: 'Nouveau champ',
    moveUp: 'Monter',
    moveDown: 'Descendre',
    archive: 'Archives',
    archiveTitle: 'Archiver le champ personnalisé',
    archiveDescription: 'Le champ « {{label}} » est masqué ; les valeurs capturées restent.',
    entities: {
      CUSTOMER: 'Partenaires commerciaux',
      CONTACT: 'Contacts',
      ADDRESS: 'Adresses et adresses de livraison',
      PRODUCT: 'Articles',
      PRODUCT_PACKAGING: "Conditionnements d'articles",
      DOCUMENT: 'En-tête du document',
      DOCUMENT_ITEM: "Ligne d'élément de document",
      BATCH: 'Lots',
    },
    documentTypes: {
      QUOTE: 'Devis',
      ORDER: 'Commande',
      DELIVERY_NOTE: 'Bon de livraison',
      INVOICE: 'Facture',
      PURCHASE_ORDER: 'Bon de commande',
    },
    formula: {
      title: 'Formule',
      expression: 'Expression',
      expressionHint:
        'Autorisés sont + - * / parenthèses, || pour les replis et les abdos, min, max, rond, sol et plafond.',
      invalid: "La formule n'est pas valide.",
      unit: 'Unité',
      unitHint: 'Affiché après le résultat, par ex. €/pièce.',
      decimals: 'Décimales',
      testTitle: 'Test en direct',
      result: 'Résultat',
      variables: {
        title: 'Variables disponibles – cliquez pour insérer',
        quantity: 'Quantité',
        unitPrice: 'Prix unitaire',
        discountPercent: '% de remise',
        taxRate: "Taux d'imposition",
        lineNet: 'Réseau de lignes',
        lineGross: 'Ligne brute',
        lineTotal: 'Total de la ligne',
      },
    },
    types: {
      WEEKLY_SCHEDULE: 'Jours de semaine et horaires',
      TEXT: 'Texte',
      TEXTAREA: 'Texte multiligne',
      NUMBER: 'Entier',
      DECIMAL: 'Décimal',
      DATE: 'Date',
      CHECKBOX: 'Oui/Non',
      SELECT: 'Choix unique',
      MULTI_SELECT: 'Choix multiples',
      FORMULA: 'Champ calculé',
    },
  },
  auditVault: {
    title: "Coffre-fort d'audit GoBD",
    subtitle: 'Historique des modifications complet et enchaîné cryptographiquement.',
    error: "Le journal n'a pas pu être chargé.",
    empty: 'Aucune entrée pour les filtres sélectionnés.',
    integrityTitle: 'Intégrité et export des données fiscales',
    integrityHint:
      'Le contrôle recalcule chaque hachage et signale les incohérences et les nombres manquants.',
    verify: 'Vérifier la chaîne de hachage',
    exportCsv: 'Exportation GoBD (CSV)',
    exportXml: 'Exportation GDPdU (XML)',
    exportReady: 'Exportation créée.',
    intact: 'Toutes les entrées {{count}} sont inchangées.',
    broken: "Violations d'intégrité {{count}} détectées.",
    verifiedAt: 'Vérifié à {{timestamp}} dans {{duration}} ms.',
    headHash: 'Somme de contrôle de la tête',
    filterEntity: "Filtrer l'entité",
    filterAction: 'Action de filtrage',
    allEntities: 'Toutes les entités',
    allActions: 'Toutes les actions',
    from: 'De',
    to: 'À',
    timestamp: 'Horodatage',
    action: 'Actions',
    entity: 'Entité',
    user: 'Utilisateur',
    hash: 'Somme de contrôle',
    previousHash: 'Prédécesseur',
    origin: 'Origine',
    diffTitle: 'Entrée de journal #{{sequence}}',
    field: 'Champ',
    before: 'Avant',
    after: 'Après',
    noPayload: 'Cette action ne transporte aucune charge utile.',
    changedFields: 'Les champs {{count}} sur {{total}} ont été modifiés.',
    actions: {
      INSERT: 'Créé',
      UPDATE: 'Mis à jour',
      DELETE: 'Supprimé',
      LOCK: 'Verrouillé',
      EXPORT: 'Exporté',
      REVERT: 'Annulé',
    },
    breaches: {
      HASH_MISMATCH: 'La somme de contrôle ne correspond pas à la charge utile.',
      CHAIN_BROKEN: 'Le lien vers le prédécesseur est rompu.',
      SEQUENCE_GAP: 'Espace dans les numéros de séquence.',
    },
    entities: {
      Customer: 'Client',
      Product: 'Article',
      ProductBatch: 'Lot',
      Document: 'Document',
      DocumentItem: 'Élément de document',
      StockMovement: 'Mouvement de stock',
      DatevExportLog: 'Exportation DATEV',
      IntrastatExportLog: 'Export Intrastat',
      CompanyProfile: "Profil de l'entreprise",
      CustomFieldDefinition: 'Champ personnalisé',
      VatIdVerification: 'Preuve de validation TVA',
      DunningSettings: 'Paramètres de relance',
      DunningStage: 'Niveau de relance',
      CustomerDunningPolicy: 'Conditions de relance client',
      DunningRun: 'Cycle de relance',
      DunningNotice: 'Relance de paiement',
      DunningNoticeSnapshot: 'Instantané de relance validé',
      DunningNoticeItem: 'Poste ouvert relancé',
      DunningStatusEvent: 'Preuve de statut de relance',
      DunningDispatch: 'Preuve d’envoi de relance',
      LedgerInvoice: 'Identité comptable de facture',
      LedgerSettlement: 'Justificatif de lettrage daté',
      LedgerPaymentOffset: 'Compensation de crédit datée',
      LedgerMoneyTransfer: 'Virement interne',
      LedgerSettings: 'Configuration comptable',
      LedgerAccount: 'Compte général',
      LedgerFiscalYear: 'Exercice comptable',
      LedgerPeriod: 'Période comptable',
      LedgerDimension: 'Centre / objet analytique',
      LedgerJournal: 'Écriture comptable',
      LedgerJournalLine: 'Ligne comptable',
      CustomerPayment: 'Paiement client',
      CustomerPaymentAllocation: 'Affectation du paiement',
      VatValidationSettings: 'Paramètres de validation TVA',
      ProductionLotGenealogyEdge: 'Lien de généalogie des lots',
      RecallSimulation: 'Simulation de rappel',
      RecallSimulationAnchor: 'Lot de départ du rappel',
      RecallSimulationAffectedBatch: 'Lot concerné par le rappel',
      RecallSimulationOperation: 'Opération de rappel',
    },
  },
  dataHub: {
    title: 'Importer et exporter',
    subtitle: 'Reprenez les données de base de fichiers CSV ou Excel.',
    step1: 'Choisir un fichier',
    step1Hint: 'CSV ou Excel par glisser-déposer ou via le sélecteur de fichiers.',
    step2: 'Colonnes de la carte',
    step2Hint: 'Les colonnes dont les noms correspondent sont pré-attribuées.',
    step3: 'Valider et importer',
    step3Hint: "L'essai à sec n'écrit rien dans la base de données.",
    target: 'Zone cible',
    dropHere: 'Déposez le fichier ici',
    formats: "Prend en charge les fichiers .csv et .xlsx jusqu'à 10 Mo.",
    chooseFile: 'Choisir un fichier',
    uploaded: '{{fileName}} · Lignes {{rows}}, colonnes {{columns}} détectées.',
    sourceColumn: 'Colonne source',
    sample: 'Exemples de valeurs',
    targetField: 'Champ ERP',
    ignore: 'Ne pas importer',
    runDryRun: "Démarrer l'essai à sec",
    validRows: '{{count}} lignes valides',
    invalidRows: '{{count}} lignes non valides',
    row: 'Ligne',
    field: 'Champ',
    reason: 'Raison',
    startImport: 'Importer les lignes {{count}}',
    imported: '{{success}} enregistrements créés, {{failed}} ignorés.',
    history: 'Historique des importations',
    historyHint: 'Les exécutions terminées peuvent être entièrement annulées.',
    noJobs: "Aucune importation n'est encore exécutée.",
    file: 'Fichier',
    progress: 'Progrès',
    status: 'Statut',
    executedAt: 'Terminé',
    rowSummary: '{{success}} importé · Erreurs {{failed}} · Total {{total}}',
    rollback: 'Revenir',
    rolledBack: 'Importation annulée.',
    entities: {
      CUSTOMER: 'Clients',
      PRODUCT: 'Articles',
      STOCK_ADJUSTMENT: 'Ajustements de stocks',
    },
    statuses: {
      PENDING: 'En attente',
      PROCESSING: "En cours d'exécution",
      COMPLETED: 'Terminé',
      FAILED: 'Échec',
      ROLLED_BACK: 'Annulé',
    },
  },
  backupCenter: backupCenterFr,
  backupRestore: backupRestoreFr,
  mailBackups: {
    title: 'Envoi de courriels',
    subtitle: 'Vérifiez le courrier sortant et la configuration SMTP.',
    smtpTitle: 'Diagnostic SMTP',
    smtpHint:
      "Vérifie la connexion, le cryptage et l'authentification sur le serveur de messagerie.",
    testConnection: 'Tester la connexion',
    sendTest: 'Envoyer un message test',
    recipient: 'Destinataire',
    testSent: 'Message de test envoyé.',
    testFailed: "Le message de test n'a pas pu être envoyé.",
    reachable: 'Accessible',
    unreachable: 'Injoignable',
    queueTitle: 'Échec des e-mails de documents',
    queueHint: 'Le renvoi est disponible directement sur le document.',
    queueEmpty: 'Aucune livraison échouée.',
    document: 'Document',
    errorMessage: 'Erreur',
    attemptedAt: 'Tentative de',
    backupsTitle: 'Sauvegardes',
    backupsHint: 'Archives cryptées (AES-256-GCM) dans le répertoire de sauvegarde.',
    createNow: 'Créer une sauvegarde maintenant',
    backupCreated: 'Sauvegarde créée.',
    noBackups: 'Aucune sauvegarde disponible pour le moment.',
    archive: 'Archives',
    size: 'Taille',
    createdAt: 'Créé à',
    checksum: 'Somme de contrôle',
    download: 'Télécharger',
    delete: 'Supprimer',
    deleted: 'Sauvegarde supprimée.',
    deleteTitle: 'Supprimer la sauvegarde ?',
    deleteDescription:
      'L\'archive "{{fileName}}" sera définitivement supprimée du serveur. Les copies déjà téléchargées ne sont pas affectées.',
    writable: 'Répertoire accessible en écriture',
    notWritable: 'Répertoire non accessible en écriture',
    dumpAvailable: 'pg_dump disponible',
    dumpMissing: "pg_dump manquant — sauvegardes exécutées via le cron de l'hôte",
    cron: 'Calendrier (cron)',
    retention: 'Rétention (jours)',
    storageTarget: 'Cible de stockage',
    encrypted: 'Chiffrer',
    scheduleActive: 'Programme actif',
    lastRun: 'Dernière exécution : {{timestamp}} · {{status}}',
    targets: {
      LOCAL: 'Volume local',
      S3: 'Compatible S3',
      OFFSITE_SSH: 'Stockage SSH hors site',
    },
  },
} as const;
